import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { usePointer } from '../hooks/usePointer';

interface Dot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  base: number; // base alpha
}

// Tunables for the signature effect.
const DENSITY = 16000; // lower = more dots (px² per dot)
const MAX_DOTS = 90;
const INFLUENCE = 200; // px radius the cursor affects
const LINK_DIST = 130; // px max distance to draw a link near the cursor

/**
 * The single, consolidated ambient layer behind all content.
 * - A soft accent spotlight follows the cursor.
 * - A sparse constellation drifts slowly; dots near the cursor brighten,
 *   ease toward it, and connect with thin lines.
 * - Respects prefers-reduced-motion (renders one calm static frame).
 * - One rAF loop, capped DPR, pauses when the tab is hidden.
 */
const AmbientBackground: React.FC = () => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointer = usePointer();
  const prefersReduced = useReducedMotion();

  // smoothed pointer for the constellation (responsive)
  const smooth = useRef({ x: 0, y: 0, init: false });
  // separate, slower smoother just for the glow so it trails the cursor
  const glow = useRef({ x: 0, y: 0, init: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Parse accent (#rrggbb) into channels once.
    const hex = theme.palette.primary.main.replace('#', '');
    const ar = parseInt(hex.substring(0, 2), 16);
    const ag = parseInt(hex.substring(2, 4), 16);
    const ab = parseInt(hex.substring(4, 6), 16);
    const accent = (a: number) => `rgba(${ar}, ${ag}, ${ab}, ${a})`;

    let width = 0;
    let height = 0;
    let dots: Dot[] = [];
    let raf = 0;
    let running = true;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const build = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(MAX_DOTS, Math.floor((width * height) / DENSITY));
      dots = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        base: Math.random() * 0.25 + 0.12,
      }));
    };

    const drawSpotlight = (px: number, py: number) => {
      const r = 380;
      const g = ctx.createRadialGradient(px, py, 0, px, py, r);
      g.addColorStop(0, accent(0.1));
      g.addColorStop(0.4, accent(0.04));
      g.addColorStop(1, accent(0));
      ctx.fillStyle = g;
      ctx.fillRect(px - r, py - r, r * 2, r * 2);
    };

    const render = (animate: boolean) => {
      ctx.clearRect(0, 0, width, height);

      // smoothed pointer target (falls back to center when inactive)
      const targetX = pointer.current.active ? pointer.current.x : width / 2;
      const targetY = pointer.current.active ? pointer.current.y : height / 2;
      if (!smooth.current.init) {
        smooth.current = { x: targetX, y: targetY, init: true };
      } else {
        smooth.current.x += (targetX - smooth.current.x) * 0.08;
        smooth.current.y += (targetY - smooth.current.y) * 0.08;
      }
      const mx = smooth.current.x;
      const my = smooth.current.y;

      // glow trails far behind the cursor. Factor chosen to match the blob's
      // 5s timing: re-targeting over a 5000ms ramp ≈ a 5s time constant, and
      // for per-frame smoothing at 60fps, τ = 1/(60·k), so k = 1/(60·5) ≈ 0.0033.
      if (!glow.current.init) {
        glow.current = { x: targetX, y: targetY, init: true };
      } else {
        glow.current.x += (targetX - glow.current.x) * 0.0346;
        glow.current.y += (targetY - glow.current.y) * 0.0346;
      }

      drawSpotlight(glow.current.x, glow.current.y);

      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];

        if (animate) {
          d.x += d.vx;
          d.y += d.vy;
          if (d.x < 0 || d.x > width) d.vx *= -1;
          if (d.y < 0 || d.y > height) d.vy *= -1;
        }

        const dx = mx - d.x;
        const dy = my - d.y;
        const dist = Math.hypot(dx, dy);
        const near = dist < INFLUENCE;
        const pull = near ? (INFLUENCE - dist) / INFLUENCE : 0;

        // gentle ease toward the cursor when nearby
        if (animate && near) {
          d.x += (dx / (dist || 1)) * pull * 0.4;
          d.y += (dy / (dist || 1)) * pull * 0.4;
        }

        const alpha = Math.min(0.9, d.base + pull * 0.6);
        const radius = 1.1 + pull * 1.6;

        ctx.beginPath();
        ctx.arc(d.x, d.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = pull > 0 ? accent(alpha) : `rgba(244, 244, 245, ${alpha})`;
        ctx.fill();

        // links — only between dots that are both within cursor influence
        if (near) {
          for (let j = i + 1; j < dots.length; j++) {
            const o = dots[j];
            const odx = mx - o.x;
            const ody = my - o.y;
            if (Math.hypot(odx, ody) > INFLUENCE) continue;
            const ldx = d.x - o.x;
            const ldy = d.y - o.y;
            const ld = Math.hypot(ldx, ldy);
            if (ld < LINK_DIST) {
              const op = (1 - ld / LINK_DIST) * pull * 0.5;
              ctx.beginPath();
              ctx.moveTo(d.x, d.y);
              ctx.lineTo(o.x, o.y);
              ctx.strokeStyle = accent(op);
              ctx.lineWidth = 0.6;
              ctx.stroke();
            }
          }
        }
      }
    };

    const loop = () => {
      if (!running) return;
      render(true);
      raf = requestAnimationFrame(loop);
    };

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!prefersReduced) {
        running = true;
        loop();
      }
    };

    let resizeTimer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        build();
        if (prefersReduced) render(false);
      }, 150);
    };

    build();

    if (prefersReduced) {
      render(false); // one calm static frame
    } else {
      loop();
    }

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [theme.palette.primary.main, pointer, prefersReduced]);

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
      {/* Faint vignette to seat the content + kill banding at edges */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(120% 120% at 50% 0%, transparent 55%, rgba(0,0,0,0.45) 100%)',
        }}
      />
      {/* Very subtle static grain */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.035,
          mixBlendMode: 'overlay',
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
    </Box>
  );
};

export default AmbientBackground;
